import { Fragment } from "react";
import type { KitchenPayload } from "../../api/types";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { startsCategory } from "../orders/Receipt";
import { describeModifier, noteText } from "../sale/modifiers";

interface Props {
  ticket: KitchenPayload;
  /** The paper of the kitchen printer. */
  paper?: 58 | 80;
}

/**
 * A kitchen ticket as the station prints it (blok I): what kind of ticket in big letters (BIJBESTELLING,
 * GESCHRAPT, GEANNULEERD and VERPLAATST inverted, so nobody mistakes them for a new order), the station,
 * the table or the kind of order, the code, when it was ordered and must be ready, then only the lines
 * this station makes with their options and notes ("Naam op de bon" when the shop set one).
 */
export function KitchenTicket({ ticket, paper = 80 }: Props) {
  const text = t.print.ticket;
  const { order } = ticket;
  const kind = ticket.ticket;
  const stop = kind === "cancelled" || kind === "removed";
  return (
    <article className={`bon kbon${paper === 58 ? " w58" : ""}`} aria-label={`${text.titles[kind]} ${order.code}`}>
      <div className={`ktitle${kind === "new" ? "" : " inv"}`}>{text.titles[kind]}</div>
      <div className="kstation">{ticket.station}</div>
      {order.channel === "table" && order.table !== null ? (
        <div className="kwhere">{text.table(order.table)}</div>
      ) : (
        <div className="kwhere">{t.sale.channels[order.channel].toUpperCase()}</div>
      )}
      <div className="kv">
        <span className="code5">{order.code}</span>
        <span>#{order.number}</span>
      </div>
      {order.name !== null && order.name !== "" && <div>{text.name(order.name)}</div>}
      <div className="kv">
        <span>{text.ordered(formatTime(order.createdAt))}</span>
        {order.readyAt !== null && (
          <b>
            {order.channel === "delivery"
              ? text.deliverAt(formatTime(order.readyAt))
              : text.ready(formatTime(order.readyAt))}
          </b>
        )}
      </div>
      {kind === "moved" && ticket.movedFrom !== null && order.table !== null && (
        <div className="kmove">
          {text.moved(ticket.movedFrom, order.table)}
          <br />
          {text.bring}
        </div>
      )}
      {ticket.reason !== null && <div className="kreason">{text.reason(ticket.reason)}</div>}
      <div className="rule" />
      {stop && <div className="knot">{text.notAnyMore}</div>}
      {ticket.lines.map((line, index) => (
        <Fragment key={index}>
          {startsCategory(ticket.lines, index) && <div className="cat">{line.category}</div>}
          <div className="kit">
            <span>{line.quantity}×</span>
            <span>
              {line.name}
              {line.variant !== null && ` ${line.variant}`}
            </span>
            {line.modifiers.map((modifier, modifierIndex) => (
              <span className="kmod" key={modifierIndex}>
                {describeModifier(modifier, false)}
              </span>
            ))}
            {line.note !== null && <span className="kmod">{noteText(line.note)}</span>}
          </div>
        </Fragment>
      ))}
      {order.note !== null && order.note !== "" && (
        <>
          <div className="rule" />
          <div className="knote">{text.note(order.note)}</div>
        </>
      )}
      <div className="rule" />
      <div className="c">{text.by(ticket.by, formatTime(ticket.at))}</div>
    </article>
  );
}
